import { Button, Col, Row, SegmentControl, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import GamePicker, { getGameLabel } from 'screens/Admin/screens/QuickQuiz/components/GamePicker';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import {
  ANCHOR_KIND,
  anchorToDraft,
  readWaveAnchors
} from '../anchors';
import { minutesToHoursLabel } from '../constants';
import {
  appendOpenAt,
  ceilToMinute,
  liveWaveOf,
  normalizeTimeline,
  trackWindow
} from '../timeline';
import { toDatetimeLocal } from '../waveEditPayload';
import CalendarZoom, { CALENDAR_RANGES } from './CalendarZoom';
import NewWaveEditor from './NewWaveEditor';
import SessionTrack from './SessionTrack';
import WaveEditorRow from './WaveEditorRow';
import { Badge, Section } from './ui';
import { Notice, fmtDateTime, fmtTime, localTzLabel } from './timelineUi';

// THE TIMELINE EDITOR — the FanWave screen's schedule surface, at two zoom levels over one
// endpoint (GET /admin/fanwave/timeline, whose response is the object at the TOP LEVEL: no `data`
// wrapper).
//
//   match zoom    ?game=<id>            one fixture: phase strip, one session track, playhead
//   calendar zoom from + to             a date range: waves as bars, fixtures as markers
//
// Every mutation refetches, without exception. A `pending` wave's resolved position moves as the
// match progresses, and a save can change which waves overlap or block each other — so the
// response after a write is the only trustworthy picture.

const ZOOMS = ['Match', 'Calendar'];
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const TimelineSection = ({ team, onTeamChange }) => {
  const teamId = team?._id || null;

  const [zoomIdx, setZoomIdx] = useState(0);
  const [gameId, setGameId] = useState(null);
  const [rangeKey, setRangeKey] = useState('week');
  const [rangeStart, setRangeStart] = useState(() => startOfDay(new Date()));

  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [games, setGames] = useState([]);
  const [listRows, setListRows] = useState(new Map());
  const [expandedWaveId, setExpandedWaveId] = useState(null);
  const [adding, setAdding] = useState(null);
  const [pendingEdge, setPendingEdge] = useState(null);
  const [queuedNotice, setQueuedNotice] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  /**
   * Anchors the CMS knows EXACTLY, keyed by waveId.
   *
   * No admin read endpoint returns a wave's `openAnchor`/`closeAnchor` — POST /create and PUT
   * /update echo the pair they were sent, but GET /list and GET /timeline report only the resolved
   * times and the derived legacy fields. So the echo is cached here for the session, and anything
   * not in it falls back to reconstructing the pair from the legacy fields (see
   * `readWaveAnchors`), which is marked "derived" in the UI because that mapping cannot express
   * every event or offset.
   */
  const [exactAnchors, setExactAnchors] = useState(new Map());

  const range = useMemo(
    () => CALENDAR_RANGES.find((r) => r.key === rangeKey) || CALENDAR_RANGES[0],
    [rangeKey]
  );
  const rangeEnd = useMemo(
    () => new Date(rangeStart.getTime() + range.days * DAY_MS - 1),
    [rangeStart, range]
  );

  const isMatchZoom = zoomIdx === 0;

  // ---- Fixtures, for the picker and the calendar's kick-off markers ---------------------------
  useEffect(() => {
    if (!teamId) {
      setGames([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await Api.post(
        '/admin/games/get',
        { teamId, sortBy: { date: -1 }, limit: 100 },
        null,
        { silent: true }
      );
      if (!cancelled && res?.games) setGames(res.games);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // ---- Channel config: the tap-target bounds and the reward defaults the rows edit against ----
  useEffect(() => {
    if (!teamId) {
      setConfig(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await Api.get(`/admin/fanwave/config?teamId=${teamId}`, null, { silent: true });
      if (!cancelled && res?.data) setConfig(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // ---- The timeline itself --------------------------------------------------------------------
  const fetchTimeline = useCallback(async () => {
    if (!teamId) {
      setTimeline(null);
      return;
    }
    if (isMatchZoom && !gameId) {
      setTimeline(null);
      return;
    }
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ teamId });
    // `gameId` and `from` are mutually exclusive — a range and a single fixture are different
    // questions, and the endpoint refuses both together.
    if (isMatchZoom) {
      params.set('gameId', gameId);
    } else {
      params.set('from', rangeStart.toISOString());
      params.set('to', rangeEnd.toISOString());
    }
    const res = await Api.get(`/admin/fanwave/timeline?${params.toString()}`, null, {
      silent: true
    });
    setLoading(false);
    if (!res || res.err) {
      setTimeline(null);
      setError(res?.err || 'Could not load the timeline.');
      return;
    }
    setTimeline(normalizeTimeline(res));
  }, [teamId, isMatchZoom, gameId, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline, refreshToken]);

  // ---- GET /list, purely to ENRICH the editor -------------------------------------------------
  // The track and both zoom levels render from the timeline response alone. This adds the two
  // things the timeline endpoint does not report and the row's fields need: a wave's `rewards`,
  // and the legacy schedule fields the derived anchor label is reconstructed from.
  useEffect(() => {
    if (!teamId) {
      setListRows(new Map());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams({ teamId, limit: '100' });
      const res = await Api.get(`/admin/fanwave/list?${params.toString()}`, null, { silent: true });
      // `{ data: { waves: [...] } }` — the rows are TWO levels down. Unwrapping one level yields an
      // object, `Array.isArray` then fails, and the enrichment silently produces nothing: no
      // rewards prefill and no legacy fields to read an edge's provenance from.
      const rows = res?.data?.waves || res?.waves || (Array.isArray(res?.data) ? res.data : []);
      if (!cancelled && Array.isArray(rows)) {
        setListRows(new Map(rows.filter((r) => r.waveId).map((r) => [r.waveId, r])));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, refreshToken]);

  const refetch = useCallback((created) => {
    // POST /create and PUT /update answer in the platform's standard `{ data, msg }` envelope —
    // unlike GET /timeline, which is the object at the top level. Accept either, because this is
    // fed from both and reading the wrong one silently loses the anchor echo.
    const echo = created?.data || created;
    // Cache the echoed pair before refetching: it is the only EXACT record of this wave's anchors
    // the CMS will have, since no read endpoint returns them.
    if (echo?.waveId && echo.openAnchor && echo.closeAnchor) {
      setExactAnchors((prev) => {
        const next = new Map(prev);
        next.set(echo.waveId, {
          openAnchor: echo.openAnchor,
          closeAnchor: echo.closeAnchor
        });
        return next;
      });
    }
    // `promoted: false` is not a failure — the wave is queued behind the one currently holding the
    // team's session and the lifecycle cron promotes it when that frees. Said out loud because
    // "created, but not running" otherwise reads as a bug.
    if (echo && echo.promoted === false) {
      setQueuedNotice(
        created?.msg ||
          'Created, and queued behind the wave currently holding this team’s session. It will open when that one closes.'
      );
    } else {
      setQueuedNotice(null);
    }
    setRefreshToken((t) => t + 1);
  }, []);

  const anchorsFor = useCallback(
    (wave) =>
      readWaveAnchors(
        { ...(listRows.get(wave.waveId) || {}), ...wave },
        {
          postFtMinutes: timeline?.postFtMinutes,
          cached: exactAnchors.get(wave.waveId)
        }
      ),
    [listRows, exactAnchors, timeline]
  );

  const game = useMemo(() => games.find((g) => g._id === gameId) || null, [games, gameId]);

  const axis = useMemo(
    () =>
      timeline && isMatchZoom
        ? trackWindow({
            phases: timeline.phases,
            waves: timeline.waves,
            now: new Date(),
            // The endpoint resolves phases only for the fixtures its WAVES run on, so a fixture
            // with no waves comes back with `phases: []` — nothing to build an axis from, which is
            // exactly when the track is needed to place the first wave. The kick-off is already
            // loaded for the picker, so it seeds the axis.
            extraStamps: [game?.date]
          })
        : null,
    [timeline, isMatchZoom, game]
  );
  const live = timeline ? liveWaveOf(timeline) : null;

  const startAdd = useCallback(
    (openAt) => {
      setExpandedWaveId(null);
      // The default open is exactly where the previous wave closes: one click for the common case,
      // and adjacency guarantees neither an overlap (the comparison is half-open) nor a gap.
      // Rounded up to the whole minute the picker can actually hold — see `ceilToMinute`. Without
      // it the seeded open truncates to a few seconds INSIDE the previous wave and the append
      // default reports an overlap against the very wave it is handing over from.
      const handover = ceilToMinute(openAt || appendOpenAt(timeline));
      setAdding({
        openDraft: handover
          ? anchorToDraft({ kind: ANCHOR_KIND.ABSOLUTE, at: handover }, toDatetimeLocal)
          : null,
        nonce: Date.now()
      });
    },
    [timeline]
  );

  const onDragEdge = useCallback((waveId, edge, anchor) => {
    setExpandedWaveId(waveId);
    // Handed to the row rather than applied here: the row owns its drafts, and a dragged edge is
    // an edit in progress, not a saved change. The nonce makes two identical drags distinct.
    setPendingEdge({ waveId, edge, anchor, nonce: Date.now() });
  }, []);

  if (!teamId) {
    return (
      <>
        <Section
          title="Timeline"
          description="Pick a channel to see its FanWave schedule on one axis."
        >
          <Col noFlex wid="100%" style={{ maxWidth: 460 }}>
            <TeamPicker team={team} onChange={onTeamChange} label={null} />
          </Col>
        </Section>
      </>
    );
  }

  return (
    <Col noFlex wid="100%" gap="16px">
      <Section
        title="Timeline"
        description={`Every wave on one wall-clock axis. Times in your local timezone (${localTzLabel}).`}
        right={
          <Row center noFlex gap="9px">
            <button
              type="button"
              onClick={() => setRefreshToken((t) => t + 1)}
              title="Refetch the timeline"
              style={{
                width: 32,
                height: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.GREY_20}`,
                borderRadius: '50%',
                background: '#fff',
                color: theme.GREY_70,
                cursor: 'pointer'
              }}
            >
              <FiRefreshCw size={14} />
            </button>
          </Row>
        }
      >
        <Col noFlex wid="100%" gap="12px">
          <Row noFlex wid="100%" gap="12px" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Col noFlex style={{ flex: '1 1 260px', maxWidth: 340 }}>
              <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                Channel
              </Text>
              <TeamPicker team={team} onChange={onTeamChange} label={null} />
            </Col>
            <Col noFlex style={{ flex: '0 0 220px' }}>
              <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                Zoom
              </Text>
              <SegmentControl
                options={ZOOMS}
                selectedIdx={zoomIdx}
                handleSelect={setZoomIdx}
                small
                wid="220px"
                textStyle={{ size: '13px' }}
              />
            </Col>
            {isMatchZoom ? (
              <Col noFlex style={{ flex: '1 1 260px', maxWidth: 360 }}>
                <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                  Fixture
                </Text>
                <GamePicker teamId={teamId} value={gameId} onChange={setGameId} required />
              </Col>
            ) : (
              <Row center noFlex gap="8px">
                <SegmentControl
                  options={CALENDAR_RANGES.map((r) => r.label)}
                  selectedIdx={CALENDAR_RANGES.findIndex((r) => r.key === rangeKey)}
                  handleSelect={(i) => setRangeKey(CALENDAR_RANGES[i].key)}
                  small
                  wid="180px"
                  textStyle={{ size: '13px' }}
                />
                <RangeNudge
                  onPrev={() =>
                    setRangeStart((d) => new Date(d.getTime() - range.days * DAY_MS))
                  }
                  onNext={() =>
                    setRangeStart((d) => new Date(d.getTime() + range.days * DAY_MS))
                  }
                  onToday={() => setRangeStart(startOfDay(new Date()))}
                />
              </Row>
            )}
          </Row>

          {!isMatchZoom && (
            <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
              {`${fmtDateTime(rangeStart)} → ${fmtDateTime(rangeEnd)}`}
            </Text>
          )}
        </Col>
      </Section>

      {loading && (
        <Row center noFlex gap="8px" pad="8px 0">
          <Spinner2 />
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12 }}>
            Loading timeline…
          </Text>
        </Row>
      )}

      {error && (
        <Notice tone="danger" title="Could not load the timeline">
          {error}
        </Notice>
      )}

      {isMatchZoom && !gameId && !loading && (
        <Col
          centerAll
          noFlex
          wid="100%"
          pad="34px 0"
          gap="4px"
          style={{
            borderRadius: 14,
            border: `1px dashed ${theme.GREY_30}`,
            background: theme.GREY_10
          }}
        >
          <Text h4 col={theme.GREY_70} marg="0">
            No fixture selected
          </Text>
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12.5 }}>
            Pick a fixture above to see its phase strip and session track.
          </Text>
        </Col>
      )}

      {timeline && isMatchZoom && (
        <Section
          title={game ? getGameLabel(game, teamId) : 'Match timeline'}
          description={
            game?.date
              ? `Kick-off ${fmtDateTime(game.date)} · post-full-time window ${
                  minutesToHoursLabel(timeline.postFtMinutes) || `${timeline.postFtMinutes}m`
                }`
              : 'Kick-off time not set for this fixture.'
          }
          right={
            live ? (
              <Badge variant="active" dot>
                {`live · closes ${fmtTime(live.closesAt)}`}
              </Badge>
            ) : (
              <Badge variant="info">{`${timeline.waves.length} wave${
                timeline.waves.length === 1 ? '' : 's'
              }`}</Badge>
            )
          }
        >
          <Col noFlex wid="100%" gap="14px">
            {axis ? (
              <>
                <SessionTrack
                  timeline={timeline}
                  window={axis}
                  anchorsFor={anchorsFor}
                  selectedWaveId={expandedWaveId}
                  onSelectWave={(id) => setExpandedWaveId((prev) => (prev === id ? null : id))}
                  onAddInGap={(gap) => startAdd(gap.from)}
                  onAddAtEnd={() => startAdd(null)}
                  onDragEdge={onDragEdge}
                />

                <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: 11 }}>
                  Solid edges are observed — the match reached that moment. Dashed edges and a “~”
                  are projected from kick-off and will move. Drag a segment handle to retime it:
                  near a phase boundary it snaps to that event, mid-phase to a match minute, and
                  outside the match to a date &amp; time.
                </Text>
              </>
            ) : (
              // No phases and no waves and no kick-off date: there is no axis to draw truthfully.
              // Anchoring to the match clock is meaningless until the fixture has a date, so the
              // editor says so rather than drawing an invented one.
              <Notice tone="warning" title="No axis to draw yet">
                This fixture has no kick-off time set, and no waves to place. A match anchor
                resolves against kick-off, so set the fixture date before scheduling a wave on the
                match clock — a wave on two date-times can still be created from the Waves tab.
              </Notice>
            )}

            {!timeline.phases.length && axis && (
              <Notice tone="info" title="Phase boundaries not available yet">
                The timeline endpoint resolves phase times only for fixtures that already have a
                wave, so the strip is empty until the first wave exists here. The axis below is
                drawn from the fixture’s kick-off; anchors still resolve normally once saved.
              </Notice>
            )}

            {!timeline.waves.length && (
              <Col
                centerAll
                noFlex
                wid="100%"
                pad="18px 0"
                gap="8px"
                style={{
                  borderRadius: 10,
                  border: `1px dashed ${theme.GREY_30}`,
                  background: theme.GREY_10
                }}
              >
                <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12 }}>
                  No waves on this fixture yet.
                </Text>
                <Button
                  text="+ Add the first wave"
                  onClick={() => startAdd(null)}
                  bg={theme.SECONDARY}
                  small
                  wid="180px"
                />
              </Col>
            )}

            {adding && (
              <NewWaveEditor
                key={adding.nonce}
                teamId={teamId}
                gameId={gameId}
                timeline={timeline}
                config={config}
                seedOpenDraft={adding.openDraft}
                onCancel={() => setAdding(null)}
                onCreated={(res) => {
                  setAdding(null);
                  refetch(res);
                }}
              />
            )}

            {queuedNotice && (
              <Notice tone="info" title="Queued behind the running wave">
                {queuedNotice}
              </Notice>
            )}

            {timeline.waves.length > 0 && (
              <Col noFlex wid="100%" gap="8px">
                <Text b2 bold marg="0" style={{ fontSize: 13 }}>
                  Waves on this fixture
                </Text>
                {timeline.waves.map((wave, i) => (
                  <WaveEditorRow
                    key={wave.waveId}
                    wave={wave}
                    listRow={listRows.get(wave.waveId) || null}
                    anchors={anchorsFor(wave)}
                    timeline={timeline}
                    config={config}
                    index={i}
                    expanded={expandedWaveId === wave.waveId}
                    onToggle={() =>
                      setExpandedWaveId((prev) => (prev === wave.waveId ? null : wave.waveId))
                    }
                    pendingEdge={
                      pendingEdge?.waveId === wave.waveId ? pendingEdge : null
                    }
                    // The update echo carries its own waveId inside the envelope; `waveId` is
                    // supplied only as the fallback for a response that omits it.
                    onSaved={(res) =>
                      refetch(res?.data ? { ...res, data: { waveId: wave.waveId, ...res.data } } : { waveId: wave.waveId, ...res })
                    }
                    onRequestCancel={async (w) => {
                      const res = await Api.post(
                        '/admin/fanwave/cancel',
                        { teamId, waveId: w.waveId },
                        null,
                        { silent: true }
                      );
                      if (res && !res.err) refetch();
                    }}
                    onRequestDelete={async (w) => {
                      const params = new URLSearchParams({ teamId, waveId: w.waveId });
                      const res = await Api.delete(
                        `/admin/fanwave/delete?${params.toString()}`,
                        null,
                        null,
                        { silent: true, raw: true }
                      );
                      if (res && !res.err) refetch();
                    }}
                  />
                ))}
              </Col>
            )}
          </Col>
        </Section>
      )}

      {timeline && !isMatchZoom && (
        <Section
          title="Calendar"
          description="Waves as bars, fixtures as kick-off markers. A bar lying across another fixture’s marker is a wave that will block that fixture’s wave — only one wave holds the session."
        >
          <CalendarZoom
            timeline={timeline}
            from={rangeStart}
            to={rangeEnd}
            games={games}
            onOpenMatch={(id) => {
              setGameId(id);
              setZoomIdx(0);
            }}
          />
        </Section>
      )}
    </Col>
  );
};

const RangeNudge = ({ onPrev, onNext, onToday }) => (
  <Row center noFlex gap="5px">
    <NudgeButton onClick={onPrev} title="Previous range">
      <FiChevronLeft size={14} />
    </NudgeButton>
    <button
      type="button"
      onClick={onToday}
      style={{
        height: 30,
        padding: '0 12px',
        border: `1px solid ${theme.GREY_20}`,
        borderRadius: 20,
        background: '#fff',
        color: theme.GREY_70,
        fontFamily: 'CeraPro',
        fontSize: 12,
        cursor: 'pointer'
      }}
    >
      Today
    </button>
    <NudgeButton onClick={onNext} title="Next range">
      <FiChevronRight size={14} />
    </NudgeButton>
  </Row>
);

const NudgeButton = ({ onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      width: 30,
      height: 30,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.GREY_20}`,
      borderRadius: '50%',
      background: '#fff',
      color: theme.GREY_70,
      cursor: 'pointer'
    }}
  >
    {children}
  </button>
);

export default TimelineSection;
